import React, { useState, useEffect, useCallback } from 'react';
import { 
  BarChart3,
  TrendingUp,
  TrendingDown,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  RefreshCw,
  Calendar,
  Zap,
  Award,
  Youtube,
  Instagram,
  Sparkles,
  ArrowRight,
  Loader2,
  Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../lib/AuthContext';
import api from '../lib/api';

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    youtube_shorts: <Youtube className={className} />,
    youtube: <Youtube className={className} />,
    tiktok: <TikTokIcon className={className} />,
    instagram_reels: <Instagram className={className} />,
    instagram: <Instagram className={className} />
  };
  return icons[platform] || <Sparkles className={className} />;
};

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || '0';
};

const StatCard = ({ title, value, icon: Icon, change, changeType, subtitle }) => (
  <Card className="glass-card">
    <CardContent className="pt-6">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{formatNumber(value)}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${
          changeType === 'up' ? 'bg-green-500/20 text-green-400' :
          changeType === 'down' ? 'bg-red-500/20 text-red-400' :
          'bg-primary/20 text-primary'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {change !== undefined && (
        <div className={`flex items-center mt-3 text-sm ${
          changeType === 'up' ? 'text-green-400' : 
          changeType === 'down' ? 'text-red-400' : 'text-muted-foreground'
        }`}>
          {changeType === 'up' && <TrendingUp className="w-4 h-4 mr-1" />}
          {changeType === 'down' && <TrendingDown className="w-4 h-4 mr-1" />}
          {change}
        </div>
      )}
    </CardContent>
  </Card>
);

const PlatformCard = ({ data }) => (
  <Card className="glass-card">
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <PlatformIcon platform={data.platform} className="w-6 h-6" />
        <CardTitle className="text-lg capitalize">
          {data.platform.replace('_', ' ')}
        </CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground">Posts</p>
          <p className="text-xl font-bold">{data.total_posts}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Views</p>
          <p className="text-xl font-bold">{formatNumber(data.total_views)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Engajamento</p>
          <p className="text-xl font-bold">{data.engagement_rate}%</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Virais</p>
          <p className="text-xl font-bold text-green-400">{data.viral_posts}</p>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-border/50">
        <p className="text-xs text-muted-foreground mb-2">Média por post</p>
        <div className="flex justify-between text-sm">
          <span><Eye className="w-4 h-4 inline mr-1" />{formatNumber(data.avg_views_per_post)}</span>
          <span><Heart className="w-4 h-4 inline mr-1" />{formatNumber(data.total_likes / data.total_posts || 0)}</span>
          <span><MessageCircle className="w-4 h-4 inline mr-1" />{formatNumber(data.total_comments / data.total_posts || 0)}</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

const TopPostCard = ({ post, rank }) => (
  <div className="flex items-center gap-4 p-3 rounded-lg bg-card/50 hover:bg-card transition-colors">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
      rank === 1 ? 'bg-yellow-500/20 text-yellow-400' :
      rank === 2 ? 'bg-gray-400/20 text-gray-300' :
      rank === 3 ? 'bg-orange-500/20 text-orange-400' :
      'bg-muted text-muted-foreground'
    }`}>
      {rank}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium truncate">{post.title}...</p>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <PlatformIcon platform={post.platform} className="w-3 h-3" />
        <span>{new Date(post.published_at).toLocaleDateString('pt-BR')}</span>
      </div>
    </div>
    <div className="text-right">
      <p className="font-bold">{formatNumber(post.views)}</p>
      <p className="text-xs text-muted-foreground">{post.engagement_rate.toFixed(1)}% eng.</p>
    </div>
  </div>
);

const InsightCard = ({ insight }) => {
  const emoji = insight.charAt(0);
  const text = insight.slice(2).trim();
  
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
      <span className="text-2xl">{emoji}</span>
      <p className="text-sm">{text}</p>
    </div>
  );
};

const MiniChart = ({ data, label }) => {
  if (!data || data.length === 0) return null;
  
  const maxValue = Math.max(...data.map(d => d.views || 0), 1);
  
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-end gap-1 h-16">
        {data.slice(-14).map((d, i) => (
          <div
            key={`${d.date}-${i}`}
            className="flex-1 bg-primary/60 rounded-t hover:bg-primary transition-colors"
            style={{ height: `${(d.views / maxValue) * 100}%`, minHeight: '2px' }}
            title={`${d.date}: ${formatNumber(d.views)} views`}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{data[0]?.date?.slice(5)}</span>
        <span>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

export default function UnifiedAnalytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');
  const [analytics, setAnalytics] = useState(null);
  
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get(`/analytics/overview?time_range=${timeRange}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/analytics/refresh');
      await fetchAnalytics();
      toast.success('Analytics atualizados!');
    } catch (error) {
      toast.error('Erro ao atualizar');
    } finally {
      setRefreshing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  
  const platforms = analytics?.platforms || {};
  const platformList = Object.values(platforms);
  
  return (
    <div className="p-6 space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary" />
            Analytics Unificado
          </h1>
          <p className="text-muted-foreground">
            Métricas de todas as plataformas em um só lugar
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hoje</SelectItem>
              <SelectItem value="7d">7 dias</SelectItem>
              <SelectItem value="30d">30 dias</SelectItem>
              <SelectItem value="90d">90 dias</SelectItem>
              <SelectItem value="all">Todo período</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total de Posts"
          value={analytics?.total_posts || 0}
          icon={BarChart3}
          subtitle="publicados no período"
        />
        <StatCard
          title="Visualizações"
          value={analytics?.total_views || 0}
          icon={Eye}
          subtitle="em todas as plataformas"
        />
        <StatCard
          title="Engajamento"
          value={`${analytics?.overall_engagement_rate || 0}%`}
          icon={Zap}
          changeType={analytics?.overall_engagement_rate > 5 ? 'up' : undefined}
          change={analytics?.overall_engagement_rate > 5 ? 'Acima da média' : undefined}
        />
        <StatCard
          title="Posts Virais"
          value={analytics?.viral_posts || 0}
          icon={Award}
          subtitle=">10k visualizações"
          changeType={analytics?.viral_posts > 0 ? 'up' : undefined}
        />
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platforms */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold">Por Plataforma</h2>
          
          {platformList.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Sem dados ainda</h3>
                <p className="text-muted-foreground">
                  Publique clips para ver analytics detalhados
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {platformList.map(platform => (
                <PlatformCard key={platform.platform} data={platform} />
              ))}
            </div>
          )}
          
          {/* Trend Chart */}
          {analytics?.daily_metrics?.length > 0 && (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base">Tendência de Views</CardTitle>
              </CardHeader>
              <CardContent>
                <MiniChart data={analytics.daily_metrics} label="Views por dia" />
              </CardContent>
            </Card>
          )}
        </div>
        
        {/* Sidebar */}
        <div className="space-y-4">
          {/* Insights */}
          {analytics?.insights?.length > 0 && (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analytics.insights.map((insight, i) => (
                  <InsightCard key={`insight-${insight.type || i}-${i}`} insight={insight} />
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Top Posts */}
          {analytics?.top_posts?.length > 0 && (
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Top Posts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {analytics.top_posts.slice(0, 5).map((post, i) => (
                  <TopPostCard key={post.clip_id} post={post} rank={i + 1} />
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Quick Stats */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-base">Resumo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Heart className="w-4 h-4" /> Likes
                </span>
                <span className="font-medium">{formatNumber(analytics?.total_likes || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Comentários
                </span>
                <span className="font-medium">{formatNumber(analytics?.total_comments || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-2">
                  <Share2 className="w-4 h-4" /> Compartilhamentos
                </span>
                <span className="font-medium">{formatNumber(analytics?.total_shares || 0)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
